/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql } from '@apollo/client';
import {
  renderDefaultError,
  renderDefaultLoading,
  cond,
  SmartComponentProps
} from '@exo/frontend-common-utils';
import { useCategoryFilters } from '../../hooks/useCategoryFilters';
import { defaultFormatLabel, formatFacetData, getSelectedFacetsList } from '../../common/filters';
import { Facet, FacetEntry } from '../../model/types';
import { CategoryRef } from '../../model/category-ref';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from '@exo/frontend-common-i18n';
import { waitForRequestsToComplete } from '../../../../../../../packages/common/apollo/client/links/activeRequests';
import { generateUrlWithLanguage } from '../../../../../../utils';
import { useSessionContext } from '@exo/frontend-common-session-context';

// TODO: Add support for CmmFacetMode
export const CategoryFiltersContainer = ({
  categoryId,
  formatLabel = defaultFormatLabel,
  currency,
  selectedFacets,
  excludeCategories = true,
  onLoad,
  renderError = renderDefaultError,
  renderLoading = renderDefaultLoading,
  render,
  hasPriceFilters
}: Props) => {
  const { loading, data, error } = useCategoryFilters<Facets>(
    {
      categoryId,
      currency,
      facets: []
    },
    CategoryFiltersContainer.fragment(!!hasPriceFilters)
  );
  const navigate = useNavigate();
  const intl = useIntl('features.catalog.catalog-ui.pages.PLP');
  const facetInStocklabel =intl.msg('label.Instock', 'In Stock');
  const facetOutStocklabel =intl.msg('lable.outofstock', 'Out of Stock');
  const AvailabilityObj: any = {
    __typename: 'CmmFacet',
    name: intl.msg('label.Availability', 'Availability'),
    multiSelect: true,
    type: 'GENERAL',
    entries: [
      {
        __typename: 'CmmFacetEntry',
        label: facetInStocklabel,
        count: null,
        code: 'InventoryFacet%3DInStock',
        state: 'unselected',
        type: 'select',
        extendedLabel: null
      },
      {
        __typename: 'CmmFacetEntry',
        label: facetOutStocklabel,
        count: null,
        code: 'InventoryFacet%3DOutofStock',
        state: 'unselected',
        type: 'select',
        extendedLabel: null
      }
    ]
  };
  const session = useSessionContext()
  useEffect(() => {
    if (!data || !onLoad) return;
    onLoad({
      selectedFacets: getSelectedFacetsList([AvailabilityObj, ...data?.products?.facets], selectedFacets, formatLabel)
    });
  }, [data, selectedFacets?.join(',')]);

  if (loading) return renderLoading();
  if (error) {
    navigate(generateUrlWithLanguage('/error',session.language))
    return;
  };
  if (!data) return null;

  const facets = data?.products?.facets?.filter(
    eachFacet =>
      eachFacet?.name !== 'ParentCatalogGroup' &&
      eachFacet?.name !== 'Price' &&
      eachFacet?.name !== 'Price (USD)'
  );
  return render({
    selectedFacets: getSelectedFacetsList([AvailabilityObj, ...facets], selectedFacets, formatLabel),
    facets: formatFacetData([AvailabilityObj, ...facets], excludeCategories, formatLabel)
  });
};

type Facets = {
  facets: Facet[];
};

CategoryFiltersContainer.fragment = (priceFilters: boolean) => gql`
  fragment CategoryFilter on PrdResultConnection {
    facets {
      name
      multiSelect
      type
      entries {
        label
        count
        code
        state
        type
        ${cond(
          priceFilters,
          `
          extendedLabel {
          ... on CmmFacetExtendedLabelMonetaryAmount {
             amount {
               value
               currency
             }
           }

           ... on CmmFacetExtendedLabelMonetaryAmountRange {
             amountLow {
              value
               currency
             }
             amountHigh {
               value
               currency
             }
           }
         }
        `
        )}
      }
    }
  }
`;

type Props = SmartComponentProps<{
  categoryId: CategoryRef;
  formatLabel?: (facet: Facet, entry: FacetEntry) => string;
  currency: string;
  selectedFacets?: string[];
  excludeCategories?: boolean;
  hasPriceFilters?: boolean;
  onLoad?: (d: any) => void;
  render: (props: CategoryFiltersContainerRenderProps) => JSX.Element;
}>;

export type CategoryFiltersContainerRenderProps = {
  selectedFacets?: ReturnType<typeof getSelectedFacetsList>;
  facets: ReturnType<typeof formatFacetData>;
};

export type SelectedFacetForPresentation = NonNullable<ReturnType<typeof getSelectedFacetsList>>[0];
export type FacetForPresentation = ReturnType<typeof formatFacetData>[0];
